import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Button,
  Box,
  Checkbox,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Grid,
  Typography
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import { saveAs } from 'file-saver';
import { useDispatch, useSelector } from 'react-redux';
import FMSModal, { FMSModalTitle } from 'src/common/FMSModal';
import SelectField from 'src/common/formFields/SelectField';
import DatePickerField from 'src/common/formFields/DatePickerField';
import DateRangeShortcuts from 'src/common/formFields/DateRangeShortcuts';
import { Form, Formik } from 'formik';
import { BASEAPI } from 'src/api/api';
import Loader from 'src/common/Loader';
import { getAllSites } from 'src/redux/siteSlice';
import { filterVehiclesBySite, getAllVehicles } from 'src/redux/vehicleSlice';
import {
  filterDispenserBySite,
  getAllDispensers
} from 'src/redux/dispenserSlice';
import { getAllVehicleGroupList } from 'src/redux/groupSlice';
import { DATE_FORMAT, REPORT_FILTER_OPTIONS } from 'src/utils/constants';
import { getAllTanks, filterTanksBySite } from 'src/redux/tankSlice';
import AutocompleteField from 'src/common/formFields/AutocompleteField';
import { getAllCards } from 'src/redux/cardSlice';
import getDefaultDateRangeValues from 'src/utils/dateDefaults';
import {
  TRANSACTION_DATE_PICKER_POPPER_SX,
  TRANSACTION_STYLE_COLORS
} from 'src/components/transactions/transactionStyleTokens';
import { getReportExportColumns } from 'src/utils/exportColumns';

const EMPTY_EXPORT_COLUMNS = [];

const TRANSACTION_REPORT_CONTROL_SX = {
  '& .MuiOutlinedInput-root': {
    '& fieldset, & .MuiOutlinedInput-notchedOutline': {
      borderColor: `${TRANSACTION_STYLE_COLORS.primary} !important`
    },
    '&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: `${TRANSACTION_STYLE_COLORS.primary} !important`
    },
    '&.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: `${TRANSACTION_STYLE_COLORS.primary} !important`
    }
  },
  '& .MuiInputLabel-root, & .MuiSvgIcon-root, & .MuiInputAdornment-root': {
    color: TRANSACTION_STYLE_COLORS.primary
  }
};

function ReportFilterModal({
  open,
  title,
  downloadUrl,
  handleClose,
  dateFormat,
  onlyDate = false,
  filterConfig = REPORT_FILTER_OPTIONS,
  formModel,
  formValidation,
  formInitValues,
  exportColumns = EMPTY_EXPORT_COLUMNS
}) {
  const dispatch = useDispatch();
  const isReconciliationReport = title === 'Reconciliation Report';
  const isOnlyDate = isReconciliationReport || onlyDate;
  const isTransactionReport = title.toLowerCase().includes('transaction');
  const resolvedExportColumns = exportColumns.length
    ? exportColumns
    : getReportExportColumns(title);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(() => resolvedExportColumns.map((column) => column.id));
  const { allSites } = useSelector((state) => state.sites);
  const { vehicleRegNumListBySite } = useSelector((state) => state.vehicles);
  const { dispenserListBySite } = useSelector((state) => state.dispensers);
  const { allVehicleGroups } = useSelector((state) => state.groups);
  const { tankListBySite } = useSelector((state) => state.tanks);
  const { allCards } = useSelector((state) => state.cards);

  const {
    formId,
    formField: {
      site,
      tank,
      address,
      assetType,
      requestStatus,
      cardNumber,
      vehicleGroup,
      vehicleNumber,
      startTime,
      endTime,
      date
    }
  } = formModel;

  useEffect(() => {
    const nextSelectedColumns = resolvedExportColumns.map((column) => column.id);
    setSelectedColumns((currentSelectedColumns) => {
      if (currentSelectedColumns.length === nextSelectedColumns.length
        && currentSelectedColumns.every((column, index) => column === nextSelectedColumns[index])) {
        return currentSelectedColumns;
      }
      return nextSelectedColumns;
    });
  }, [resolvedExportColumns, open]);

  useEffect(() => {
    if (open) {
      setError(false);
      if (filterConfig.site) {
        dispatch(getAllSites());
      }
      if (filterConfig.vehicle) {
        dispatch(getAllVehicles());
      }
      if (filterConfig.dispenser) {
        dispatch(getAllDispensers());
      }
      if (filterConfig.vehicleGroup) {
        dispatch(getAllVehicleGroupList());
      }
      if (filterConfig.tank) {
        dispatch(getAllTanks());
      }
      if (filterConfig.card) {
        dispatch(getAllCards());
      }
    }
  }, [open]);

  const downloadReport = async (values) => {
    if (resolvedExportColumns.length && !selectedColumns.length) {
      setError('Select at least one column to export.');
      return;
    }
    setLoading(true);
    setError(false);
    const isExternalSingleDate = title === 'External Transaction Report' && Boolean(date?.name);
    const keyList = Object.keys(values).filter((key) => !(isExternalSingleDate && key === date.name));
    let queryParams = '';
    keyList.forEach((key) => {
      if (values[key] && values[key] !== 'all') {
        queryParams += `&${key}=${values[key]}`;
      }
    });
    if (isExternalSingleDate && values[date.name]) {
      const selectedDate = moment(values[date.name]).format(DATE_FORMAT.unixDate);
      queryParams += `&date_time__gte=${selectedDate}T00:00:00`;
      queryParams += `&date_time__lte=${selectedDate}T23:59:59`;
    }
    if (selectedColumns.length) {
      queryParams += `&columns=${encodeURIComponent(selectedColumns.join(','))}`;
    }
    const apiUrl = downloadUrl + queryParams;
    BASEAPI.get(apiUrl, { responseType: 'blob', timeout: 20000 })
      .then((res) => {
        if (res.status !== 200) {
          throw res;
        }
        const fileName = `${title.toLowerCase().replace(/\s+/g, ' ')} ${moment().format(DATE_FORMAT.reportDateTime)}.xlsx`;
        saveAs(res.data, fileName);
        handleClose();
      })
      .catch((err) => {
        const errMsg = err.status === 204 ? 'No data available! Try with different filters.'
          : 'Error downloading the report. Try again later!';
        setError(errMsg);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // filter tank list based on site id on site change
  const onSiteChange = (e, fn, setValue) => {
    const selectedSiteId = e.target.value;
    if (filterConfig.vehicle) {
      dispatch(filterVehiclesBySite(selectedSiteId));
      setValue(vehicleNumber.name, 'all');
    }
    if (filterConfig.dispenser) {
      dispatch(filterDispenserBySite(selectedSiteId));
      setValue(address.name, 'all');
    }
    if (filterConfig.tank) {
      dispatch(filterTanksBySite(selectedSiteId));
      setValue(tank.name, 'all');
    }
    fn(e);
  };

  return (
    <FMSModal open={open} onClose={handleClose} maxWidth="md" fullWidth keepMounted>
      <FMSModalTitle
        title={`DOWNLOAD ${title}`.toUpperCase()}
        onCancel={handleClose}
        sx={isTransactionReport ? { borderBottomColor: TRANSACTION_STYLE_COLORS.primary } : undefined}
      />
      <Divider />
      <Formik
        initialValues={getDefaultDateRangeValues({
          initialValues: formInitValues,
          startName: startTime?.name,
          endName: endTime?.name,
          dateName: date?.name,
          onlyDate: isOnlyDate,
          startAtStartOfDay: isTransactionReport
        })}
        validationSchema={formValidation}
        onSubmit={downloadReport}
      >
        {({ isSubmitting, handleChange, setFieldValue }) => (
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              flex: '1 1 auto',
              minHeight: 0,
              overflow: 'hidden'
            }}
            aria-busy={loading}
          >
            <Form
              id={formId}
              style={{
                display: 'flex',
                flex: '1 1 auto',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden'
              }}
            >
              <DialogContent sx={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}>
                <Grid container spacing={3}>
                  {filterConfig.site && (
                    <Grid item xs={12} sm={6}>
                      <SelectField
                        {...site}
                        onChange={(e) => onSiteChange(e, handleChange, setFieldValue)}
                        data={[...site.data, ...allSites]}
                        fullWidth
                        sx={isTransactionReport ? TRANSACTION_REPORT_CONTROL_SX : undefined}
                      />
                    </Grid>
                  )}
                  {filterConfig.dispenser && (
                    <Grid item xs={12} sm={6}>
                      <SelectField
                        {...address}
                        data={[...address.data, ...dispenserListBySite]}
                        fullWidth
                        sx={isTransactionReport ? TRANSACTION_REPORT_CONTROL_SX : undefined}
                      />
                    </Grid>
                  )}
                  {filterConfig.tank && (
                    <Grid item xs={12} sm={6}>
                      <SelectField
                        {...tank}
                        data={[...tank.data, ...tankListBySite]}
                        fullWidth
                        sx={isTransactionReport ? TRANSACTION_REPORT_CONTROL_SX : undefined}
                      />
                    </Grid>
                  )}
                  {filterConfig.card && (
                    <Grid item xs={12} sm={6}>
                      <AutocompleteField
                        name={cardNumber.name}
                        label={cardNumber.label}
                        options={[...cardNumber.data, ...allCards]}
                        getOptionLabel={(option) => option.uid}
                        fullWidth
                        sx={isTransactionReport ? TRANSACTION_REPORT_CONTROL_SX : undefined}
                      />
                    </Grid>
                  )}
                  {filterConfig.vehicle && (
                    <Grid item xs={12} sm={6}>
                      <AutocompleteField
                        name={vehicleNumber.name}
                        label={vehicleNumber.label}
                        options={[...vehicleNumber.data, ...vehicleRegNumListBySite]}
                        getOptionLabel={(option) => option.registration_num}
                        fullWidth
                        sx={isTransactionReport ? TRANSACTION_REPORT_CONTROL_SX : undefined}
                      />
                    </Grid>
                  )}
                  {filterConfig.vehicleGroup && (
                    <Grid item xs={12} sm={6}>
                      <SelectField
                        {...vehicleGroup}
                        data={[...vehicleGroup.data, ...allVehicleGroups]}
                        fullWidth
                        sx={isTransactionReport ? TRANSACTION_REPORT_CONTROL_SX : undefined}
                      />
                    </Grid>
                  )}
                  {filterConfig.assetType && (
                    <Grid item xs={12} sm={6}>
                      <SelectField
                        {...assetType}
                        fullWidth
                        sx={isTransactionReport ? TRANSACTION_REPORT_CONTROL_SX : undefined}
                      />
                    </Grid>
                  )}
                  {filterConfig.requestStatus && (
                    <Grid item xs={12} sm={6}>
                      <SelectField
                        {...requestStatus}
                        fullWidth
                        sx={isTransactionReport ? TRANSACTION_REPORT_CONTROL_SX : undefined}
                      />
                    </Grid>
                  )}
                  {date && (
                    <Grid item xs={12} sm={6}>
                      <DatePickerField
                        dateFormat={dateFormat}
                        onlyDate={isOnlyDate}
                        nativeInput={!isTransactionReport}
                        {...date}
                        required
                        disableFuture
                        pickerIconColor={isTransactionReport ? TRANSACTION_STYLE_COLORS.primary : undefined}
                        pickerPopperSx={isTransactionReport ? TRANSACTION_DATE_PICKER_POPPER_SX : undefined}
                        sx={isTransactionReport ? TRANSACTION_REPORT_CONTROL_SX : undefined}
                      />
                    </Grid>
                  )}
                  {!date && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <DatePickerField
                        dateFormat={dateFormat}
                        onlyDate={isOnlyDate}
                        nativeInput={!isTransactionReport}
                        {...startTime}
                        required
                        disableFuture
                        pickerIconColor={isTransactionReport ? TRANSACTION_STYLE_COLORS.primary : undefined}
                        pickerPopperSx={isTransactionReport ? TRANSACTION_DATE_PICKER_POPPER_SX : undefined}
                        sx={isTransactionReport ? TRANSACTION_REPORT_CONTROL_SX : undefined}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DatePickerField
                        dateFormat={dateFormat}
                        onlyDate={isOnlyDate}
                        nativeInput={!isTransactionReport}
                        {...endTime}
                        required
                        disableFuture
                        pickerIconColor={isTransactionReport ? TRANSACTION_STYLE_COLORS.primary : undefined}
                        pickerPopperSx={isTransactionReport ? TRANSACTION_DATE_PICKER_POPPER_SX : undefined}
                        sx={isTransactionReport ? TRANSACTION_REPORT_CONTROL_SX : undefined}
                      />
                    </Grid>
                  </>
                  )}
                  {!date && startTime && endTime && (
                  <Grid item xs={12}>
                    <DateRangeShortcuts
                      startName={startTime.name}
                      endName={endTime.name}
                      onlyDate={isOnlyDate}
                      dateFormat={dateFormat}
                    />
                  </Grid>
                  )}
                  {resolvedExportColumns.length > 0 && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          border: '1px solid rgba(11, 106, 153, 0.24)',
                          borderRadius: 1.5,
                          p: 1.5,
                          borderColor: TRANSACTION_STYLE_COLORS.primary,
                          backgroundColor: 'rgba(11, 110, 153, 0.04)'
                        }}
                      >
                        <Typography variant="caption" sx={{ color: TRANSACTION_STYLE_COLORS.primary, fontWeight: 700 }}>
                          Columns to export
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                            maxHeight: 150,
                            overflowY: 'auto',
                            mt: 0.5
                          }}
                        >
                          {resolvedExportColumns.map((column) => (
                            <FormControlLabel
                              key={column.id}
                              control={(
                                <Checkbox
                                  checked={selectedColumns.includes(column.id)}
                                  onChange={() => setSelectedColumns((current) => (
                                    current.includes(column.id)
                                      ? current.filter((id) => id !== column.id)
                                      : [...current, column.id]
                                  ))}
                                  size="small"
                                  sx={{ '&.Mui-checked': { color: TRANSACTION_STYLE_COLORS.primary } }}
                                />
                              )}
                              label={column.label}
                              sx={{ color: '#0F172A', m: 0 }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
                {error && (
                  <Typography
                    variant="subtitle1"
                    sx={{ color: 'red', pt: 2, textAlign: 'center' }}
                  >
                    {error || ''}
                  </Typography>
                )}
              </DialogContent>
              <DialogActions sx={{ m: 2, flexShrink: 0 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleClose}
                  sx={{ backgroundColor: TRANSACTION_STYLE_COLORS.primary, borderColor: TRANSACTION_STYLE_COLORS.primary, '&:hover': { backgroundColor: TRANSACTION_STYLE_COLORS.primary, borderColor: TRANSACTION_STYLE_COLORS.primary, filter: 'brightness(0.95)' } }}
                >
                  Close
                </Button>
                <Button
                  disabled={isSubmitting || loading}
                  type="submit"
                  size="large"
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  sx={{ backgroundColor: TRANSACTION_STYLE_COLORS.primary, borderColor: TRANSACTION_STYLE_COLORS.primary, '&:hover': { backgroundColor: TRANSACTION_STYLE_COLORS.primary, borderColor: TRANSACTION_STYLE_COLORS.primary, filter: 'brightness(0.95)' } }}
                >
                  Export
                </Button>
              </DialogActions>
            </Form>
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255, 255, 255, 0.72)',
                  zIndex: 1
                }}
              >
                <Loader sx={{ minHeight: 220 }} />
              </Box>
            )}
          </Box>
        )}
      </Formik>
    </FMSModal>
  );
}

ReportFilterModal.propTypes = {
  open: PropTypes.bool.isRequired,
  hideDisp: PropTypes.bool,
  filterConfig: PropTypes.object,
  formModel: PropTypes.object.isRequired,
  formValidation: PropTypes.object.isRequired,
  formInitValues: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  dateFormat: PropTypes.string,
  onlyDate: PropTypes.bool,
  downloadUrl: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
  exportColumns: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  }))
};

export default ReportFilterModal;
